import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Users, ChevronRight, ArrowLeft, Search, ArrowUpDown, ArrowUp, ArrowDown, Pencil, Check, X, Download, AlertTriangle, Clock, TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend, PieChart, Pie, Cell } from "recharts";
import { toast } from "sonner";
import { downloadFile } from "../lib/download";
import { format } from "date-fns";
import { useVisibilityOverride } from "../context/VisibilityOverrideContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function money(v) {
  return `£${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export default function DirectorProjectManagement() {
  const [view, setView] = useState("drilldown"); // "drilldown" | "flat"
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [loading, setLoading] = useState(false);
  const { includeExcluded } = useVisibilityOverride();

  // Drilldown state
  const [managers, setManagers] = useState([]);
  const [selectedManagerId, setSelectedManagerId] = useState(null);
  const [managerPortfolio, setManagerPortfolio] = useState(null);
  const [managerSearch, setManagerSearch] = useState("");

  // Flat list state
  const [flatProjects, setFlatProjects] = useState([]);
  const [flatTotal, setFlatTotal] = useState(0);
  const [flatPage, setFlatPage] = useState(1);
  const [flatPageSize, setFlatPageSize] = useState(50);
  const [flatSearch, setFlatSearch] = useState("");
  const [flatSort, setFlatSort] = useState("total_hours");
  const [flatOrder, setFlatOrder] = useState("desc");
  const [flatTotalPages, setFlatTotalPages] = useState(0);

  // Flat list drill-down state (Manager-style full-page detail)
  const [flatSelectedProject, setFlatSelectedProject] = useState(null);
  const [flatProjectDetail, setFlatProjectDetail] = useState(null);
  const [flatDetailLoading, setFlatDetailLoading] = useState(false);
  const [flatPersonEntries, setFlatPersonEntries] = useState(null);
  const [flatPersonLoading, setFlatPersonLoading] = useState(false);

  const fetchManagers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (dateRange.start) params.set("start_date", dateRange.start);
      if (dateRange.end) params.set("end_date", dateRange.end);
      if (includeExcluded) params.set("include_excluded", "true");
      const qs = params.toString() ? `?${params.toString()}` : "";
      const res = await axios.get(`${API}/director/managers-overview${qs}`);
      setManagers(res.data.managers || []);
    } catch (e) {
      toast.error("Failed to load managers overview");
    } finally {
      setLoading(false);
    }
  }, [dateRange, includeExcluded]);

  const fetchManagerPortfolio = async (uid) => {
    try {
      setSelectedManagerId(uid);
      const params = new URLSearchParams();
      if (dateRange.start) params.set("start_date", dateRange.start);
      if (dateRange.end) params.set("end_date", dateRange.end);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const res = await axios.get(`${API}/director/manager/${uid}/portfolio${qs}`);
      setManagerPortfolio(res.data);
    } catch (e) {
      toast.error("Failed to load manager portfolio");
    }
  };

  const fetchFlatProjects = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (dateRange.start) params.set("start_date", dateRange.start);
      if (dateRange.end) params.set("end_date", dateRange.end);
      params.set("page", String(flatPage));
      params.set("page_size", String(flatPageSize));
      params.set("sort", flatSort);
      params.set("order", flatOrder);
      if (flatSearch.trim()) params.set("search", flatSearch.trim());
      if (includeExcluded) params.set("include_excluded", "true");
      const res = await axios.get(`${API}/director/all-projects?${params.toString()}`);
      setFlatProjects(res.data.projects || []);
      setFlatTotal(res.data.total || 0);
      setFlatTotalPages(res.data.total_pages || 0);
    } catch (e) {
      toast.error("Failed to load projects list");
    } finally {
      setLoading(false);
    }
  }, [dateRange, flatPage, flatPageSize, flatSort, flatOrder, flatSearch, includeExcluded]);

  useEffect(() => {
    if (view === "drilldown") fetchManagers();
  }, [view, fetchManagers]);

  useEffect(() => {
    if (view === "flat" && !flatSelectedProject) fetchFlatProjects();
  }, [view, fetchFlatProjects, flatSelectedProject]);

  // Close detail panels when date range changes
  useEffect(() => {
    setFlatSelectedProject(null);
    setFlatProjectDetail(null);
    setFlatPersonEntries(null);
  }, [dateRange.start, dateRange.end]);

  const filteredManagers = managers.filter(m => {
    if (!managerSearch.trim()) return true;
    const t = managerSearch.toLowerCase();
    return `${m.name} ${m.email} ${m.role}`.toLowerCase().includes(t);
  });

  const toggleSort = (col) => {
    if (flatSort === col) {
      setFlatOrder(flatOrder === "asc" ? "desc" : "asc");
    } else {
      setFlatSort(col);
      setFlatOrder("desc");
    }
    setFlatPage(1);
  };

  const SortIcon = ({ col }) => {
    if (flatSort !== col) return <ArrowUpDown className="w-3 h-3 inline ml-1 opacity-40" />;
    return flatOrder === "asc" ? <ArrowUp className="w-3 h-3 inline ml-1" /> : <ArrowDown className="w-3 h-3 inline ml-1" />;
  };

  // Flat list: fetch project detail (Director endpoint — no assignment check)
  const fetchFlatProjectDetail = async (pid) => {
    setFlatDetailLoading(true);
    setFlatSelectedProject(pid);
    setFlatPersonEntries(null);
    try {
      const params = new URLSearchParams();
      if (dateRange.start) params.set("start_date", dateRange.start);
      if (dateRange.end) params.set("end_date", dateRange.end);
      if (includeExcluded) params.set("include_excluded", "true");
      const qs = params.toString() ? `?${params.toString()}` : "";
      const res = await axios.get(`${API}/director/project/${encodeURIComponent(pid)}/detail${qs}`);
      setFlatProjectDetail(res.data);
    } catch (error) {
      toast.error("Failed to load project detail");
    } finally {
      setFlatDetailLoading(false);
    }
  };

  // Flat list: fetch person entries within a project
  const fetchFlatPersonEntries = async (personnel) => {
    if (!flatSelectedProject) return;
    setFlatPersonLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateRange.start) params.set("start_date", dateRange.start);
      if (dateRange.end) params.set("end_date", dateRange.end);
      if (includeExcluded) params.set("include_excluded", "true");
      const qs = params.toString() ? `?${params.toString()}` : "";
      const res = await axios.get(`${API}/director/project/${encodeURIComponent(flatSelectedProject)}/person/${encodeURIComponent(personnel)}${qs}`);
      setFlatPersonEntries(res.data);
    } catch (error) {
      toast.error("Failed to load entries");
    } finally {
      setFlatPersonLoading(false);
    }
  };

  // Back to flat list from detail view
  const handleBackToFlatList = () => {
    setFlatSelectedProject(null);
    setFlatProjectDetail(null);
    setFlatPersonEntries(null);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="director-project-management">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="secondary" className="bg-emerald-100 text-emerald-800">Director</Badge>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">Project Management</h1>
          <p className="text-muted-foreground mt-1">All projects across all managers</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">From</Label>
            <Input type="date" value={dateRange.start} onChange={(e) => setDateRange(p => ({ ...p, start: e.target.value }))} className="h-9 w-36" data-testid="date-from" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">To</Label>
            <Input type="date" value={dateRange.end} onChange={(e) => setDateRange(p => ({ ...p, end: e.target.value }))} className="h-9 w-36" data-testid="date-to" />
          </div>
          <Button variant="outline" size="sm" className="mt-5" onClick={() => setDateRange({ start: "", end: "" })} disabled={!dateRange.start && !dateRange.end}>Clear</Button>
        </div>
      </div>

      {/* Only show tabs when NOT in flat detail drill-down */}
      {!flatSelectedProject ? (
        <Tabs value={view} onValueChange={setView}>
          <TabsList>
            <TabsTrigger value="drilldown" className="gap-2"><Users className="w-4 h-4" />Drilldown by Manager</TabsTrigger>
            <TabsTrigger value="flat" className="gap-2"><Search className="w-4 h-4" />All Projects (Flat List)</TabsTrigger>
          </TabsList>

          {/* DRILLDOWN VIEW */}
          <TabsContent value="drilldown" className="space-y-6 mt-6">
            {!selectedManagerId ? (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <CardTitle className="text-base font-semibold">All Project Managers</CardTitle>
                      <CardDescription>{managers.length} users with project assignments — click to view their portfolio</CardDescription>
                    </div>
                    <Input placeholder="Search managers..." value={managerSearch} onChange={(e) => setManagerSearch(e.target.value)} className="w-64" data-testid="manager-search" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Name</th>
                          <th className="text-left py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Role</th>
                          <th className="text-right py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Projects</th>
                          <th className="text-right py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Total Hours</th>
                          <th className="text-right py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Billable</th>
                          <th className="text-right py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Utilization</th>
                          <th className="text-right py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Team</th>
                          <th className="py-2.5 px-1"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredManagers.map(m => (
                          <tr key={m.manager_user_id} className="border-b border-border/50 hover:bg-muted/30 cursor-pointer transition-[background-color] duration-150" onClick={() => fetchManagerPortfolio(m.manager_user_id)}>
                            <td className="py-2.5 px-3 font-medium">
                              <div>{m.name}</div>
                              <div className="text-xs text-muted-foreground">{m.email}</div>
                            </td>
                            <td className="py-2.5 px-3"><Badge variant="secondary" className="text-xs">{m.role}</Badge></td>
                            <td className="py-2.5 px-3 text-right tabular-nums">{m.assigned_projects_count}</td>
                            <td className="py-2.5 px-3 text-right tabular-nums">{m.total_hours}h</td>
                            <td className="py-2.5 px-3 text-right tabular-nums text-emerald-600">{m.fee_hours}h</td>
                            <td className="py-2.5 px-3 text-right tabular-nums">{m.utilization_rate}%</td>
                            <td className="py-2.5 px-3 text-right tabular-nums">{m.team_size}</td>
                            <td className="py-2.5 px-1"><ChevronRight className="w-4 h-4 text-muted-foreground" /></td>
                          </tr>
                        ))}
                        {filteredManagers.length === 0 && (
                          <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">{loading ? "Loading..." : "No project managers found. Assign projects from the Admin page."}</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <ManagerDrillDown
                managerId={selectedManagerId}
                portfolio={managerPortfolio}
                dateRange={dateRange}
                onBack={() => { setSelectedManagerId(null); setManagerPortfolio(null); }}
                refreshPortfolio={fetchManagerPortfolio}
              />
            )}
          </TabsContent>

          {/* FLAT LIST VIEW */}
          <TabsContent value="flat" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <CardTitle className="text-base font-semibold">All Projects</CardTitle>
                    <CardDescription>{flatTotal} total projects across the company — click a project for detailed analysis</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input placeholder="Search project, company, PM..." value={flatSearch} onChange={(e) => { setFlatSearch(e.target.value); setFlatPage(1); }} className="w-64" data-testid="flat-search" />
                    <Select value={String(flatPageSize)} onValueChange={(v) => { setFlatPageSize(Number(v)); setFlatPage(1); }}>
                      <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                        <SelectItem value="200">200</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="flat-projects-table">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer" onClick={() => toggleSort("project_id")}>Project<SortIcon col="project_id" /></th>
                        <th className="text-left py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer" onClick={() => toggleSort("company")}>Company<SortIcon col="company" /></th>
                        <th className="text-left py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Project Manager(s)</th>
                        <th className="text-right py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer" onClick={() => toggleSort("total_hours")}>Total Hours<SortIcon col="total_hours" /></th>
                        <th className="text-right py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer" onClick={() => toggleSort("fee_hours")}>Billable<SortIcon col="fee_hours" /></th>
                        <th className="text-right py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer" onClick={() => toggleSort("non_fee_hours")}>Non-Billable<SortIcon col="non_fee_hours" /></th>
                        <th className="text-right py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer" onClick={() => toggleSort("team_size")}>Team<SortIcon col="team_size" /></th>
                        <th className="text-right py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer" onClick={() => toggleSort("entries_count")}>Entries<SortIcon col="entries_count" /></th>
                        <th className="py-2.5 px-1"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {flatProjects.map(p => (
                        <tr key={p.project_id} className="border-b border-border/50 hover:bg-muted/30 cursor-pointer transition-[background-color] duration-150" onClick={() => fetchFlatProjectDetail(p.project_id)} data-testid={`flat-project-row-${p.project_id}`}>
                          <td className="py-2.5 px-3 font-medium max-w-[220px] truncate">{p.project_id}</td>
                          <td className="py-2.5 px-3 text-muted-foreground">{p.company || "—"}</td>
                          <td className="py-2.5 px-3">
                            {p.project_managers.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {p.project_managers.map(pm => (
                                  <Badge key={pm.id} variant="secondary" className="text-xs">{pm.name} <span className="text-muted-foreground ml-1">({pm.role})</span></Badge>
                                ))}
                              </div>
                            ) : <span className="text-xs text-muted-foreground italic">unassigned</span>}
                          </td>
                          <td className="py-2.5 px-3 text-right tabular-nums">{p.total_hours}h</td>
                          <td className="py-2.5 px-3 text-right tabular-nums text-emerald-600">{p.fee_hours}h</td>
                          <td className="py-2.5 px-3 text-right tabular-nums text-amber-600">{p.non_fee_hours}h</td>
                          <td className="py-2.5 px-3 text-right tabular-nums">{p.team_size}</td>
                          <td className="py-2.5 px-3 text-right tabular-nums">{p.entries_count}</td>
                          <td className="py-2.5 px-1"><ChevronRight className="w-4 h-4 text-muted-foreground" /></td>
                        </tr>
                      ))}
                      {flatProjects.length === 0 && (
                        <tr><td colSpan={9} className="py-8 text-center text-muted-foreground">{loading ? "Loading..." : "No projects found"}</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {flatTotalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                    <p className="text-xs text-muted-foreground">
                      Page {flatPage} of {flatTotalPages} · {flatTotal} total projects
                    </p>
                    <div className="flex items-center gap-1">
                      <Button variant="outline" size="sm" onClick={() => setFlatPage(1)} disabled={flatPage === 1}>First</Button>
                      <Button variant="outline" size="sm" onClick={() => setFlatPage(flatPage - 1)} disabled={flatPage === 1}>Prev</Button>
                      <span className="px-3 text-sm tabular-nums">{flatPage}</span>
                      <Button variant="outline" size="sm" onClick={() => setFlatPage(flatPage + 1)} disabled={flatPage >= flatTotalPages}>Next</Button>
                      <Button variant="outline" size="sm" onClick={() => setFlatPage(flatTotalPages)} disabled={flatPage >= flatTotalPages}>Last</Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      ) : (
        /* ========== FLAT LIST DRILL-DOWN: Manager-Style Two-Column Layout ========== */
        <FlatProjectDrillDown
          projectId={flatSelectedProject}
          projectDetail={flatProjectDetail}
          detailLoading={flatDetailLoading}
          personEntries={flatPersonEntries}
          personLoading={flatPersonLoading}
          flatProjects={flatProjects}
          dateRange={dateRange}
          onBack={handleBackToFlatList}
          onSelectProject={fetchFlatProjectDetail}
          onSelectPerson={fetchFlatPersonEntries}
          onClearPerson={() => setFlatPersonEntries(null)}
          flatSearch={flatSearch}
        />
      )}
    </div>
  );
}


/* ====================================================================
   FlatProjectDrillDown — Manager-style two-column project detail view
   Exactly mirrors the ManagerDashboard layout
   ==================================================================== */
function FlatProjectDrillDown({
  projectId,
  projectDetail,
  detailLoading,
  personEntries,
  personLoading,
  flatProjects,
  dateRange,
  onBack,
  onSelectProject,
  onSelectPerson,
  onClearPerson,
  flatSearch,
}) {
  const COLORS = ["#6366f1","#f59e0b","#10b981","#ef4444","#8b5cf6","#ec4899","#14b8a6","#f97316","#06b6d4","#84cc16"];

  // Filter projects for the left panel
  const filteredProjects = flatProjects.filter(p => {
    if (!flatSearch.trim()) return true;
    const s = flatSearch.toLowerCase();
    return p.project_id.toLowerCase().includes(s) || (p.company || "").toLowerCase().includes(s);
  });

  return (
    <div className="space-y-6" data-testid="flat-project-drilldown">
      {/* Back button */}
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" onClick={onBack} className="gap-2" data-testid="back-to-flat-list">
          <ArrowLeft className="w-4 h-4" /> Back to All Projects
        </Button>
        <div>
          <h2 className="text-xl font-semibold">{projectId}</h2>
          <p className="text-xs text-muted-foreground">{projectDetail?.master?.company || projectDetail?.master?.status || "Project detail view"}</p>
        </div>
      </div>

      {/* Two-column layout — exactly like Manager Dashboard */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        {/* LEFT: Projects List */}
        <div className="xl:col-span-7">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <CardTitle className="text-base font-semibold">All Projects</CardTitle>
                  <CardDescription>Click a project to see detailed analysis</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto max-h-[500px]">
                <table className="w-full text-sm" data-testid="drilldown-project-list">
                  <thead className="sticky top-0 bg-background border-b border-border">
                    <tr>
                      <th className="text-left py-2 px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Project</th>
                      <th className="text-right py-2 px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Total</th>
                      <th className="text-right py-2 px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Billable</th>
                      <th className="text-right py-2 px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Non-Bill</th>
                      <th className="text-right py-2 px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Team</th>
                      <th className="py-2 px-1"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProjects.map((p) => (
                      <tr
                        key={p.project_id}
                        className={`border-b border-border/50 hover:bg-muted/30 cursor-pointer transition-[background-color] duration-150 ${projectId === p.project_id ? "bg-primary/5" : ""}`}
                        onClick={() => onSelectProject(p.project_id)}
                        data-testid={`drilldown-row-${p.project_id}`}
                      >
                        <td className="py-2.5 px-2">
                          <div>
                            <p className="font-medium truncate max-w-[200px]">{p.project_id}</p>
                            <p className="text-xs text-muted-foreground">{p.company || "—"}</p>
                          </div>
                        </td>
                        <td className="py-2.5 px-2 text-right tabular-nums font-medium">{p.total_hours}h</td>
                        <td className="py-2.5 px-2 text-right tabular-nums text-emerald-600">{p.fee_hours}h</td>
                        <td className="py-2.5 px-2 text-right tabular-nums text-amber-600">{p.non_fee_hours}h</td>
                        <td className="py-2.5 px-2 text-right tabular-nums">{p.team_size}</td>
                        <td className="py-2.5 px-1"><ChevronRight className="w-4 h-4 text-muted-foreground" /></td>
                      </tr>
                    ))}
                    {filteredProjects.length === 0 && (
                      <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">No projects found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Pie Charts — Hours Breakdown (same as Manager) */}
          {projectDetail && projectDetail.by_personnel?.length > 0 && (() => {
            const prepData = projectDetail.by_personnel.filter(p => p.prep_hours > 0).map(p => ({ name: p.personnel, value: p.prep_hours }));
            const deliveryData = projectDetail.by_personnel.filter(p => p.delivery_hours > 0).map(p => ({ name: p.personnel, value: p.delivery_hours }));
            const hasPrepData = prepData.length > 0;
            const hasDeliveryData = deliveryData.length > 0;
            if (!hasPrepData && !hasDeliveryData) return null;
            return (
              <Card className="mt-4">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold">Team Hours Breakdown — {projectId}</CardTitle>
                  <CardDescription>Who entered how much for prep vs delivery</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className={`grid gap-4 ${hasPrepData && hasDeliveryData ? "grid-cols-2" : "grid-cols-1"}`}>
                    {hasPrepData && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground text-center mb-1">Prep Hours</p>
                        <ResponsiveContainer width="100%" height={180}>
                          <PieChart>
                            <Pie data={prepData} cx="50%" cy="50%" innerRadius={35} outerRadius={70} paddingAngle={2} dataKey="value" label={({ name, value }) => `${name.split(' ')[0]} ${value}h`} labelLine={false}>
                              {prepData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                            </Pie>
                            <Tooltip formatter={(v) => `${v}h`} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                    {hasDeliveryData && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground text-center mb-1">Delivery Hours</p>
                        <ResponsiveContainer width="100%" height={180}>
                          <PieChart>
                            <Pie data={deliveryData} cx="50%" cy="50%" innerRadius={35} outerRadius={70} paddingAngle={2} dataKey="value" label={({ name, value }) => `${name.split(' ')[0]} ${value}h`} labelLine={false}>
                              {deliveryData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                            </Pie>
                            <Tooltip formatter={(v) => `${v}h`} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })()}
        </div>

        {/* RIGHT: Project Analysis Panel (sticky, exactly like Manager) */}
        <div className="xl:col-span-5">
          <Card className="sticky top-20" data-testid="project-analysis-panel">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base font-semibold">Project Analysis</CardTitle>
                  <CardDescription>{projectId || "Select a project"}</CardDescription>
                </div>
                {projectId && (
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="sm" className="gap-1" data-testid="export-excel-btn" onClick={async () => {
                      try {
                        const exportParams = new URLSearchParams();
                        if (dateRange.start) exportParams.set("start_date", dateRange.start);
                        if (dateRange.end) exportParams.set("end_date", dateRange.end);
                        const exportQs = exportParams.toString() ? `?${exportParams.toString()}` : "";
                        await downloadFile(`${API}/export/excel/project/${encodeURIComponent(projectId)}${exportQs}`);
                        toast.success("Excel downloaded");
                      } catch { toast.error("Export failed"); }
                    }}>
                      <Download className="w-3.5 h-3.5" /> Excel
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {detailLoading ? (
                <div className="flex items-center justify-center h-48"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
              ) : projectDetail ? (
                <div className="space-y-4">
                  {/* Summary KPIs */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg bg-muted/50">
                      <p className="text-xs text-muted-foreground">Total Billed Hours</p>
                      <p className="text-xl font-bold tabular-nums" data-testid="detail-total-hours">{projectDetail.summary.total_hours}h</p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/50">
                      <p className="text-xs text-muted-foreground">Value</p>
                      <p className="text-xl font-bold tabular-nums" data-testid="detail-value">{money(projectDetail.summary.project_value_gbp)}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-emerald-500/10">
                      <p className="text-xs text-muted-foreground">Billable</p>
                      <p className="text-xl font-bold text-emerald-600 tabular-nums" data-testid="detail-billable">{projectDetail.summary.fee_hours}h</p>
                    </div>
                    <div className="p-3 rounded-lg bg-amber-500/10">
                      <p className="text-xs text-muted-foreground">Non-Billable</p>
                      <p className="text-xl font-bold text-amber-600 tabular-nums" data-testid="detail-non-billable">{projectDetail.summary.non_fee_hours}h</p>
                    </div>
                  </div>

                  {/* Quoted vs Actual */}
                  <div className="p-4 rounded-lg border border-border">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium">Quoted Hours (Prep & Delivery)</h4>
                    </div>
                    {projectDetail.summary.total_quoted > 0 ? (
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-muted-foreground">Prep: {projectDetail.summary.prep_quoted}h quoted</span><span className="font-medium">{projectDetail.summary.prep_actual}h actual</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Delivery: {projectDetail.summary.delivery_quoted}h quoted</span><span className="font-medium">{projectDetail.summary.delivery_actual}h actual</span></div>
                        <div className="border-t pt-2 flex justify-between font-medium">
                          <span>Total: {projectDetail.summary.total_quoted}h quoted</span>
                          <span className={projectDetail.summary.overrun > 0 ? "text-red-600" : "text-emerald-600"}>
                            {projectDetail.summary.overrun > 0 ? `+${projectDetail.summary.overrun}h overrun` : `${projectDetail.summary.remaining_hours}h remaining`}
                          </span>
                        </div>
                        <Progress value={Math.min(100, (projectDetail.summary.total_hours / projectDetail.summary.total_quoted) * 100)} className="h-2" />
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
                        <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-amber-800">No quoted hours set</p>
                          <p className="text-xs text-amber-600">This project does not have quoted prep & delivery hours configured</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Prep / Delivery Overrun Alerts */}
                  {projectDetail.summary.total_quoted > 0 && (() => {
                    const s = projectDetail.summary;
                    const prepOver = s.prep_quoted > 0 && s.prep_actual > s.prep_quoted;
                    const deliveryOver = s.delivery_quoted > 0 && s.delivery_actual > s.delivery_quoted;
                    if (!prepOver && !deliveryOver) return null;
                    return (
                      <div className="space-y-2">
                        {prepOver && (
                          <div className="flex items-center gap-2 p-2.5 rounded-lg bg-red-50 border border-red-200">
                            <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
                            <div className="flex-1">
                              <p className="text-xs font-semibold text-red-800">Prep Hours Exceeded</p>
                              <p className="text-xs text-red-600">{s.prep_actual}h actual vs {s.prep_quoted}h quoted — <strong className="text-red-700">+{(s.prep_actual - s.prep_quoted).toFixed(1)}h over</strong></p>
                            </div>
                          </div>
                        )}
                        {deliveryOver && (
                          <div className="flex items-center gap-2 p-2.5 rounded-lg bg-red-50 border border-red-200">
                            <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
                            <div className="flex-1">
                              <p className="text-xs font-semibold text-red-800">Delivery Hours Exceeded</p>
                              <p className="text-xs text-red-600">{s.delivery_actual}h actual vs {s.delivery_quoted}h quoted — <strong className="text-red-700">+{(s.delivery_actual - s.delivery_quoted).toFixed(1)}h over</strong></p>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Team Members — click to see entries */}
                  <div>
                    <h4 className="text-sm font-medium mb-2">Team ({projectDetail.by_personnel?.length || 0}) - Click to see entries</h4>
                    <div className="space-y-1 max-h-[200px] overflow-y-auto">
                      {(projectDetail.by_personnel || []).map(p => (
                        <div key={p.personnel}
                          className={`flex items-center justify-between p-2 rounded cursor-pointer transition-[background-color] duration-150 ${personEntries?.personnel === p.personnel ? "bg-primary/10 border border-primary/30" : "hover:bg-muted/30"}`}
                          onClick={() => onSelectPerson(p.personnel)}
                          data-testid={`team-member-${p.personnel}`}>
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                              <span className="text-xs font-semibold text-primary">{p.personnel.split(" ").map(n => n[0]).join("")}</span>
                            </div>
                            <div>
                              <span className="text-sm font-medium">{p.personnel}</span>
                              <span className="text-xs text-muted-foreground ml-2">Prep: {p.prep_hours}h · Del: {p.delivery_hours}h</span>
                            </div>
                          </div>
                          <span className="text-sm tabular-nums font-medium">{p.total_hours}h</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Person Entries Detail */}
                  {personLoading && (
                    <div className="flex items-center justify-center py-4"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" /></div>
                  )}
                  {personEntries && !personLoading && (
                    <div className="border border-border rounded-lg overflow-hidden" data-testid="person-entries-panel">
                      <div className="p-3 bg-primary/5 border-b border-border flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">{personEntries.personnel} - Entries for this project</p>
                          <p className="text-xs text-muted-foreground">{personEntries.total_hours}h total · {personEntries.fee_hours}h billable · {personEntries.entries_count} entries</p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={onClearPerson}>Close</Button>
                      </div>
                      <div className="max-h-[250px] overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="sticky top-0 bg-background">
                            <tr className="border-b border-border">
                              <th className="text-left py-1.5 px-2 font-semibold text-muted-foreground">Date</th>
                              <th className="text-left py-1.5 px-2 font-semibold text-muted-foreground">Description</th>
                              <th className="text-right py-1.5 px-2 font-semibold text-muted-foreground">Hours</th>
                              <th className="text-left py-1.5 px-2 font-semibold text-muted-foreground">Type</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(personEntries.entries || []).map((e, i) => (
                              <tr key={i} className="border-b border-border/50 hover:bg-muted/20">
                                <td className="py-1.5 px-2 text-muted-foreground whitespace-nowrap">{e.date}</td>
                                <td className="py-1.5 px-2 max-w-[200px] truncate" title={e.description}>{e.description || "-"}</td>
                                <td className="py-1.5 px-2 text-right tabular-nums font-medium">{e.hours}</td>
                                <td className="py-1.5 px-2">
                                  <Badge className={`text-[10px] px-1.5 py-0 ${e.fee_earning?.includes("Non-Fee") ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
                                    {e.fee_earning?.includes("Delivery") ? "Delivery" : e.fee_earning?.includes("Prep") ? "Prep" : e.fee_earning?.includes("Non-Fee") ? "Non-Fee" : "Fee"}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                            {(personEntries.entries || []).length === 0 && (
                              <tr><td colSpan={4} className="py-4 text-center text-muted-foreground">No entries found</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Daily Trend */}
                  {projectDetail.daily_trend?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium mb-2">Daily Trend</h4>
                      <div className="h-[150px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={projectDetail.daily_trend} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={(v) => { try { return format(new Date(v), "MMM d"); } catch { return v; } }} />
                            <YAxis tick={{ fontSize: 9 }} />
                            <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px", fontSize: 12 }} />
                            <Area type="monotone" dataKey="fee" name="Billable" stroke="#10B981" fill="#10B98130" />
                            <Area type="monotone" dataKey="non_fee" name="Non-Billable" stroke="#F59E0B" fill="#F59E0B30" />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-48 text-muted-foreground">
                  <p>Select a project to view analysis</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Team Performance — below the two-column layout */}
      {projectDetail && projectDetail.by_personnel?.length > 0 && (
        <Card data-testid="team-performance-table">
          <CardHeader>
            <CardTitle className="text-base font-semibold">Team Performance — {projectId}</CardTitle>
            <CardDescription>People working on this project</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Consultant</th>
                    <th className="text-right py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Total</th>
                    <th className="text-right py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Billable</th>
                    <th className="text-right py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Non-Billable</th>
                    <th className="text-right py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Utilization</th>
                  </tr>
                </thead>
                <tbody>
                  {projectDetail.by_personnel.map(s => {
                    const util = s.total_hours > 0 ? Math.round(s.fee_hours / s.total_hours * 100) : 0;
                    return (
                      <tr key={s.personnel} className="border-b border-border/50 hover:bg-muted/30 transition-[background-color] duration-150">
                        <td className="py-2.5 px-3 font-medium">{s.personnel}</td>
                        <td className="py-2.5 px-3 text-right tabular-nums">{s.total_hours}h</td>
                        <td className="py-2.5 px-3 text-right tabular-nums text-emerald-600">{s.fee_hours}h</td>
                        <td className="py-2.5 px-3 text-right tabular-nums text-amber-600">{s.non_fee_hours}h</td>
                        <td className="py-2.5 px-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-16 h-2 rounded-full bg-muted overflow-hidden">
                              <div className="h-full rounded-full" style={{ width: `${util}%`, backgroundColor: util >= 50 ? '#10B981' : '#F59E0B' }} />
                            </div>
                            <span className="tabular-nums text-xs">{util}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


/* ====================================================================
   ManagerDrillDown — The existing drilldown-by-manager component
   ==================================================================== */
function ManagerDrillDown({ managerId, portfolio, dateRange, onBack, refreshPortfolio }) {
  const target = portfolio?.target_user;
  const teamStats = portfolio?.team_stats || [];

  // Consultant entries drill-down state
  const [selectedConsultant, setSelectedConsultant] = useState(null);
  const [consultantData, setConsultantData] = useState(null);
  const [consultantLoading, setConsultantLoading] = useState(false);
  const [projectDetailData, setProjectDetailData] = useState(null);
  const [showHoursEditor, setShowHoursEditor] = useState(null);
  const [entryPage, setEntryPage] = useState(1);
  const [entrySort, setEntrySort] = useState("date");
  const [entryOrder, setEntryOrder] = useState("desc");

  // Person entries drill-down within inline project detail
  const [inlinePersonEntries, setInlinePersonEntries] = useState(null);
  const [inlinePersonLoading, setInlinePersonLoading] = useState(false);

  const fetchInlinePersonEntries = async (personnel, projectId) => {
    setInlinePersonLoading(true);
    try {
      const prms = new URLSearchParams();
      if (dateRange.start) prms.set("start_date", dateRange.start);
      if (dateRange.end) prms.set("end_date", dateRange.end);
      const qstr = prms.toString() ? `?${prms.toString()}` : "";
      const res = await axios.get(`${API}/manager/project/${encodeURIComponent(projectId)}/person/${encodeURIComponent(personnel)}${qstr}`);
      setInlinePersonEntries(res.data);
    } catch {
      toast.error("Failed to load person entries");
    } finally {
      setInlinePersonLoading(false);
    }
  };

  // Hours editing state
  const [editingProject, setEditingProject] = useState(null);
  const [hoursForm, setHoursForm] = useState({ prep: 0, delivery: 0 });
  const [hoursSaving, setHoursSaving] = useState(false);

  const saveProjectHours = async () => {
    if (!editingProject) return;
    setHoursSaving(true);
    try {
      await axios.put(`${API}/director/manager/${managerId}/project/${encodeURIComponent(editingProject)}/hours`, {
        prep_hours: hoursForm.prep,
        delivery_hours: hoursForm.delivery,
      });
      toast.success("Project hours updated");
      setEditingProject(null);
      if (refreshPortfolio) refreshPortfolio(managerId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update hours");
    } finally {
      setHoursSaving(false);
    }
  };

  const fetchConsultantEntries = async (personnel, page = 1, sort = "date", order = "desc") => {
    try {
      setConsultantLoading(true);
      setSelectedConsultant(personnel);
      setEntryPage(page);
      setEntrySort(sort);
      setEntryOrder(order);
      const params = new URLSearchParams({ personnel, page: String(page), page_size: "50", sort, order });
      if (dateRange.start) params.set("start_date", dateRange.start);
      if (dateRange.end) params.set("end_date", dateRange.end);
      const res = await axios.get(`${API}/director/manager/${managerId}/consultant-entries?${params.toString()}`);
      setConsultantData(res.data);
    } catch (e) {
      toast.error("Failed to load consultant entries");
    } finally {
      setConsultantLoading(false);
    }
  };

  const toggleEntrySort = (col) => {
    const newOrder = (entrySort === col && entryOrder === "desc") ? "asc" : "desc";
    fetchConsultantEntries(selectedConsultant, 1, col, newOrder);
  };

  const EntrySortIcon = ({ col }) => {
    if (entrySort !== col) return <ArrowUpDown className="w-3 h-3 inline ml-1 opacity-40" />;
    return entryOrder === "asc" ? <ArrowUp className="w-3 h-3 inline ml-1" /> : <ArrowDown className="w-3 h-3 inline ml-1" />;
  };

  // If a consultant is selected, show their detailed entries
  if (selectedConsultant && consultantData) {
    const summary = consultantData.summary || {};
    const projectHoursMap = {};
    for (const e of (consultantData.entries || [])) {
      const pid = e.project_id || "Unknown";
      if (!projectHoursMap[pid]) {
        projectHoursMap[pid] = { project_id: pid, total_hours: 0, fee_hours: 0, non_fee_hours: 0, company: e.company || "" };
      }
      projectHoursMap[pid].total_hours += (e.hours || 0);
      if (e.fee_earning && e.fee_earning.includes("Fee Earning") && !e.fee_earning.includes("Non-Fee")) {
        projectHoursMap[pid].fee_hours += (e.hours || 0);
      } else {
        projectHoursMap[pid].non_fee_hours += (e.hours || 0);
      }
    }
    for (const pid of (summary.projects_worked || [])) {
      if (!projectHoursMap[pid]) {
        projectHoursMap[pid] = { project_id: pid, total_hours: 0, fee_hours: 0, non_fee_hours: 0, company: "" };
      }
    }
    const projectHoursList = Object.values(projectHoursMap).sort((a, b) => b.total_hours - a.total_hours);

    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => { setSelectedConsultant(null); setConsultantData(null); }} className="gap-2">
            <ArrowLeft className="w-4 h-4" /> Back to Team Performance
          </Button>
          <div>
            <h2 className="text-xl font-semibold">{selectedConsultant}</h2>
            <p className="text-xs text-muted-foreground">Time entries for {target?.name}'s assigned projects</p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card><CardContent className="pt-6"><p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Total Recorded Hours</p><p className="text-2xl font-bold tabular-nums">{summary.total_hours || 0}h</p></CardContent></Card>
          <Card><CardContent className="pt-6"><p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Billable</p><p className="text-2xl font-bold text-emerald-600 tabular-nums">{summary.fee_hours || 0}h</p></CardContent></Card>
          <Card><CardContent className="pt-6"><p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Non-Billable</p><p className="text-2xl font-bold text-amber-600 tabular-nums">{summary.non_fee_hours || 0}h</p></CardContent></Card>
          <Card><CardContent className="pt-6"><p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Utilization</p><p className="text-2xl font-bold tabular-nums">{summary.utilization_rate || 0}%</p><Progress value={summary.utilization_rate || 0} className="mt-2 h-2" /></CardContent></Card>
        </div>

        {projectHoursList.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold">Assigned Projects ({summary.projects_count || projectHoursList.length})</CardTitle>
              <CardDescription>Hours breakdown per project</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Project</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Company</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">Recorded Hrs</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">Billable</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">Non-Billable</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projectHoursList.map(p => (
                      <tr key={p.project_id} className="border-b border-border/50 hover:bg-muted/30 transition-[background-color] duration-150">
                        <td className="py-2 px-3 font-medium">{p.project_id}</td>
                        <td className="py-2 px-3 text-muted-foreground">{p.company || "—"}</td>
                        <td className="py-2 px-3 text-right tabular-nums font-medium whitespace-nowrap">{p.total_hours.toFixed(1)}h</td>
                        <td className="py-2 px-3 text-right tabular-nums text-emerald-600 whitespace-nowrap">{p.fee_hours.toFixed(1)}h</td>
                        <td className="py-2 px-3 text-right tabular-nums text-amber-600 whitespace-nowrap">{p.non_fee_hours.toFixed(1)}h</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-base font-semibold">Time Entries ({consultantData.total || 0})</CardTitle>
            <CardDescription>All timesheet entries for {selectedConsultant}</CardDescription>
          </CardHeader>
          <CardContent>
            {consultantLoading ? (
              <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer select-none" onClick={() => toggleEntrySort("date")}>Date<EntrySortIcon col="date" /></th>
                        <th className="text-left py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer select-none" onClick={() => toggleEntrySort("project_id")}>Project<EntrySortIcon col="project_id" /></th>
                        <th className="text-left py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer select-none" onClick={() => toggleEntrySort("company")}>Company<EntrySortIcon col="company" /></th>
                        <th className="text-left py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer select-none" onClick={() => toggleEntrySort("description")}>Description<EntrySortIcon col="description" /></th>
                        <th className="text-right py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer select-none" onClick={() => toggleEntrySort("hours")}>Hours<EntrySortIcon col="hours" /></th>
                        <th className="text-left py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer select-none" onClick={() => toggleEntrySort("fee_earning")}>Type<EntrySortIcon col="fee_earning" /></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(consultantData.entries || []).map((e, i) => {
                        const isBillable = e.fee_earning && e.fee_earning.includes("Fee Earning") && !e.fee_earning.includes("Non-Fee");
                        return (
                          <tr key={`${e.date}-${e.project_id}-${i}`} className="border-b border-border/50 hover:bg-muted/30 transition-[background-color] duration-150">
                            <td className="py-2 px-3 tabular-nums whitespace-nowrap text-muted-foreground">{e.date || "—"}</td>
                            <td className="py-2 px-3 font-medium max-w-[180px] truncate">{e.project_id || "—"}</td>
                            <td className="py-2 px-3 text-muted-foreground max-w-[150px] truncate">{e.company || "—"}</td>
                            <td className="py-2 px-3 max-w-[280px] truncate" title={e.description}>{e.description || "—"}</td>
                            <td className="py-2 px-3 text-right tabular-nums font-medium">{e.hours || 0}h</td>
                            <td className="py-2 px-3">
                              <Badge variant="secondary" className={`text-xs ${isBillable ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                                {isBillable ? "Billable" : "Non-Billable"}
                              </Badge>
                            </td>
                          </tr>
                        );
                      })}
                      {(consultantData.entries || []).length === 0 && (
                        <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">No entries found</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {(consultantData.total_pages || 0) > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                    <p className="text-xs text-muted-foreground">
                      Page {consultantData.page} of {consultantData.total_pages} · {consultantData.total} total entries
                    </p>
                    <div className="flex items-center gap-1">
                      <Button variant="outline" size="sm" onClick={() => fetchConsultantEntries(selectedConsultant, 1, entrySort, entryOrder)} disabled={entryPage === 1}>First</Button>
                      <Button variant="outline" size="sm" onClick={() => fetchConsultantEntries(selectedConsultant, entryPage - 1, entrySort, entryOrder)} disabled={entryPage === 1}>Prev</Button>
                      <span className="px-3 text-sm tabular-nums">{entryPage}</span>
                      <Button variant="outline" size="sm" onClick={() => fetchConsultantEntries(selectedConsultant, entryPage + 1, entrySort, entryOrder)} disabled={entryPage >= consultantData.total_pages}>Next</Button>
                      <Button variant="outline" size="sm" onClick={() => fetchConsultantEntries(selectedConsultant, consultantData.total_pages, entrySort, entryOrder)} disabled={entryPage >= consultantData.total_pages}>Last</Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" onClick={onBack} className="gap-2"><ArrowLeft className="w-4 h-4" /> Back to Managers</Button>
        <div>
          <h2 className="text-xl font-semibold">{target?.name || "Loading..."}</h2>
          <p className="text-xs text-muted-foreground">{target?.email} · {target?.role}</p>
        </div>
      </div>

      {!portfolio ? (
        <div className="flex items-center justify-center h-48"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <Card><CardContent className="pt-6"><p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Assigned Projects</p><p className="text-3xl font-bold tabular-nums">{portfolio.assigned_projects_count || 0}</p></CardContent></Card>
            <Card><CardContent className="pt-6"><p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Total Hours</p><p className="text-3xl font-bold tabular-nums">{portfolio.total_hours || 0}h</p></CardContent></Card>
            <Card><CardContent className="pt-6"><p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Billable</p><p className="text-3xl font-bold text-emerald-600 tabular-nums">{portfolio.fee_hours || 0}h</p></CardContent></Card>
            <Card><CardContent className="pt-6"><p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Utilization</p><p className="text-3xl font-bold tabular-nums">{portfolio.utilization_rate || 0}%</p><Progress value={portfolio.utilization_rate || 0} className="mt-2 h-2" /></CardContent></Card>
            <Card><CardContent className="pt-6"><p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Team Size</p><p className="text-3xl font-bold tabular-nums">{portfolio.team_size || 0}</p></CardContent></Card>
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base font-semibold">Assigned Projects ({portfolio.assigned_project_ids?.length || 0})</CardTitle>
                  <CardDescription>Click a project to see detailed analysis</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2 mb-3">
                {(portfolio.assigned_project_ids || []).map(pid => (
                  <Badge key={pid} variant={editingProject === pid ? "default" : "secondary"} className={`text-xs cursor-pointer transition-all duration-150 ${editingProject === pid ? "ring-2 ring-primary" : "hover:bg-primary/10"}`}
                    onClick={async () => {
                      if (editingProject === pid) { setEditingProject(null); setInlinePersonEntries(null); return; }
                      setEditingProject(pid);
                      setInlinePersonEntries(null);
                      try {
                        const res = await axios.get(`${API}/director/project/${encodeURIComponent(pid)}/detail`);
                        setProjectDetailData(res.data);
                      } catch { setProjectDetailData(null); }
                    }}>
                    {pid}
                  </Badge>
                ))}
                {(!portfolio.assigned_project_ids || portfolio.assigned_project_ids.length === 0) && (
                  <p className="text-sm text-muted-foreground">No projects assigned</p>
                )}
              </div>

              {editingProject && projectDetailData && (
                <div className="space-y-4 animate-fade-in border border-border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold">{editingProject}</h4>
                    <Button variant="ghost" size="sm" onClick={() => { setEditingProject(null); setProjectDetailData(null); }}>Close</Button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="p-3 rounded-lg bg-muted/50"><p className="text-xs text-muted-foreground">Total Hours</p><p className="text-xl font-bold tabular-nums">{projectDetailData.summary?.total_hours || 0}h</p></div>
                    <div className="p-3 rounded-lg bg-emerald-500/10"><p className="text-xs text-muted-foreground">Billable</p><p className="text-xl font-bold text-emerald-600 tabular-nums">{projectDetailData.summary?.fee_hours || 0}h</p></div>
                    <div className="p-3 rounded-lg bg-amber-500/10"><p className="text-xs text-muted-foreground">Non-Billable</p><p className="text-xl font-bold text-amber-600 tabular-nums">{projectDetailData.summary?.non_fee_hours || 0}h</p></div>
                    <div className="p-3 rounded-lg bg-muted/50"><p className="text-xs text-muted-foreground">Value</p><p className="text-xl font-bold tabular-nums">£{Number(projectDetailData.summary?.project_value_gbp || 0).toLocaleString()}</p></div>
                  </div>
                  {(projectDetailData.summary?.total_quoted > 0 || projectDetailData.summary?.prep_quoted > 0) && (
                    <div className="p-3 rounded-lg border border-border space-y-2 text-sm">
                      <h5 className="font-medium">Quoted vs Actual</h5>
                      <div className="flex justify-between"><span className="text-muted-foreground">Prep: {projectDetailData.summary?.prep_quoted || 0}h quoted</span><span>{projectDetailData.summary?.prep_actual || 0}h actual</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Delivery: {projectDetailData.summary?.delivery_quoted || 0}h quoted</span><span>{projectDetailData.summary?.delivery_actual || 0}h actual</span></div>
                      <div className="border-t pt-2 flex justify-between font-medium">
                        <span>Total: {projectDetailData.summary?.total_quoted || 0}h</span>
                        <span className={(projectDetailData.summary?.overrun || 0) > 0 ? "text-red-600" : "text-emerald-600"}>
                          {(projectDetailData.summary?.overrun || 0) > 0 ? `+${projectDetailData.summary.overrun}h overrun` : `${projectDetailData.summary?.remaining_hours || 0}h remaining`}
                        </span>
                      </div>
                    </div>
                  )}
                  <div>
                    <h5 className="text-sm font-medium mb-2">Team ({(projectDetailData.by_personnel || []).length}) — Click to see entries</h5>
                    <div className="space-y-1 max-h-[200px] overflow-y-auto">
                      {(projectDetailData.by_personnel || []).map(p => (
                        <div key={p.personnel} className={`flex items-center justify-between p-2 rounded cursor-pointer transition-[background-color] duration-150 ${inlinePersonEntries?.personnel === p.personnel ? "bg-primary/10 border border-primary/30" : "hover:bg-muted/30"}`}
                          onClick={() => fetchInlinePersonEntries(p.personnel, editingProject)}>
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center"><span className="text-[10px] font-semibold text-primary">{p.personnel.split(" ").map(n => n[0]).join("")}</span></div>
                            <span className="text-sm">{p.personnel}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm tabular-nums font-medium whitespace-nowrap">{p.total_hours}h</span>
                            <ChevronRight className="w-3 h-3 text-muted-foreground" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {inlinePersonLoading && (
                    <div className="flex items-center justify-center py-4"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" /></div>
                  )}
                  {inlinePersonEntries && !inlinePersonLoading && (
                    <div className="border border-border rounded-lg overflow-hidden">
                      <div className="p-3 bg-primary/5 border-b border-border flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">{inlinePersonEntries.personnel} — entries for {editingProject}</p>
                          <p className="text-xs text-muted-foreground">{inlinePersonEntries.total_hours}h total · {inlinePersonEntries.fee_hours}h billable · {inlinePersonEntries.entries_count} entries</p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => setInlinePersonEntries(null)}>Close</Button>
                      </div>
                      <div className="max-h-[250px] overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="sticky top-0 bg-background">
                            <tr className="border-b border-border">
                              <th className="text-left py-1.5 px-2 font-semibold text-muted-foreground">Date</th>
                              <th className="text-left py-1.5 px-2 font-semibold text-muted-foreground">Description</th>
                              <th className="text-right py-1.5 px-2 font-semibold text-muted-foreground">Hours</th>
                              <th className="text-left py-1.5 px-2 font-semibold text-muted-foreground">Type</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(inlinePersonEntries.entries || []).map((e, i) => (
                              <tr key={i} className="border-b border-border/50 hover:bg-muted/20">
                                <td className="py-1.5 px-2 text-muted-foreground whitespace-nowrap">{e.date}</td>
                                <td className="py-1.5 px-2 max-w-[200px] truncate" title={e.description}>{e.description || "-"}</td>
                                <td className="py-1.5 px-2 text-right tabular-nums font-medium whitespace-nowrap">{e.hours}</td>
                                <td className="py-1.5 px-2">
                                  <Badge className={`text-[10px] px-1.5 py-0 ${e.fee_earning?.includes("Non-Fee") ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
                                    {e.fee_earning?.includes("Delivery") ? "Delivery" : e.fee_earning?.includes("Prep") ? "Prep" : e.fee_earning?.includes("Non-Fee") ? "Non-Fee" : "Fee"}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                            {(inlinePersonEntries.entries || []).length === 0 && (
                              <tr><td colSpan={4} className="py-4 text-center text-muted-foreground">No entries found</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => { setEditingProject(null); setProjectDetailData(null); setHoursForm({ prep: 0, delivery: 0 }); setShowHoursEditor(editingProject); }}>Edit Quoted Hours</Button>
                  </div>
                </div>
              )}

              {showHoursEditor && (
                <div className="p-4 rounded-lg border border-primary/30 bg-primary/5 space-y-3 mt-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">Set Hours for: <span className="text-primary">{showHoursEditor}</span></p>
                    <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => setShowHoursEditor(null)}>Close</Button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-muted-foreground">Prep Hours</label>
                      <input type="number" min="0" step="0.01" value={hoursForm.prep} onChange={(e) => setHoursForm(f => ({ ...f, prep: parseFloat(e.target.value) || 0 }))} className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-muted-foreground">Delivery Hours</label>
                      <input type="number" min="0" step="0.01" value={hoursForm.delivery} onChange={(e) => setHoursForm(f => ({ ...f, delivery: parseFloat(e.target.value) || 0 }))} className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm tabular-nums">{(hoursForm.prep + hoursForm.delivery).toFixed(0)}h total</span>
                    <Button size="sm" className="gap-1 text-xs" disabled={hoursSaving} onClick={saveProjectHours}><Check className="w-3 h-3" /> {hoursSaving ? "Saving..." : "Save Hours"}</Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base font-semibold">Team Performance</CardTitle>
                  <CardDescription>
                    {editingProject
                      ? `People working on ${editingProject} — click a consultant to view their entries`
                      : "People working on these projects — click a consultant to view their entries"}
                  </CardDescription>
                </div>
                {editingProject && (
                  <Badge variant="secondary" className="text-xs gap-1">
                    Filtered: {editingProject}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => { setEditingProject(null); setProjectDetailData(null); }} />
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Consultant</th>
                      <th className="text-right py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Total Recorded Hrs</th>
                      <th className="text-right py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Billable</th>
                      <th className="text-right py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Non-Billable</th>
                      <th className="text-right py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Utilization</th>
                      <th className="text-right py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Projects</th>
                      <th className="py-2.5 px-1"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(editingProject && projectDetailData
                      ? (projectDetailData.by_personnel || []).map(p => ({
                          personnel: p.personnel,
                          total_hours: p.total_hours || 0,
                          fee_hours: p.fee_hours || p.delivery_hours || 0,
                          non_fee_hours: p.non_fee_hours || p.prep_hours || 0,
                          utilization_rate: p.total_hours > 0 ? Math.round(((p.fee_hours || p.delivery_hours || 0) / p.total_hours) * 100) : 0,
                          projects_count: 1,
                        }))
                      : teamStats
                    ).map(t => (
                      <tr key={t.personnel} className="border-b border-border/50 hover:bg-muted/30 cursor-pointer transition-[background-color] duration-150" onClick={() => fetchConsultantEntries(t.personnel)}>
                        <td className="py-2.5 px-3 font-medium text-primary hover:underline">{t.personnel}</td>
                        <td className="py-2.5 px-3 text-right tabular-nums">{t.total_hours}h</td>
                        <td className="py-2.5 px-3 text-right tabular-nums text-emerald-600">{t.fee_hours}h</td>
                        <td className="py-2.5 px-3 text-right tabular-nums text-amber-600">{t.non_fee_hours}h</td>
                        <td className="py-2.5 px-3 text-right tabular-nums">{t.utilization_rate}%</td>
                        <td className="py-2.5 px-3 text-right tabular-nums">{t.projects_count}</td>
                        <td className="py-2.5 px-1"><ChevronRight className="w-4 h-4 text-muted-foreground" /></td>
                      </tr>
                    ))}
                    {((editingProject && projectDetailData) ? (projectDetailData.by_personnel || []) : teamStats).length === 0 && (
                      <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">No team activity recorded</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
